import {
  AmbientLight,
  Color,
  DirectionalLight,
  HemisphereLight,
  PointLight,
} from 'three';

let lights = null;

export function initLighting(scene, options = {}) {
  const {
    ambientIntensity = 0.9,
    hemiIntensity = 1.8,
    sunIntensity = 2.2,
    shadows = true,
    shadowMapSize = 2048,
  } = options;

  // Soft blue-white fill from above
  const ambient = new AmbientLight(0xb0d0ff, ambientIntensity);

  // Sky/ground hemisphere — cool blue sky, dark indigo ground
  const hemi = new HemisphereLight(0x8ec5fc, 0x1a1a3a, hemiIntensity);

  // Primary sun — warm directional with sharp shadows
  const sun = new DirectionalLight(0xfff4e0, sunIntensity);
  sun.position.set(25, 42, 19);
  sun.castShadow = shadows;
  sun.shadow.mapSize.set(shadowMapSize, shadowMapSize);
  sun.shadow.camera.left = -50;
  sun.shadow.camera.right = 50;
  sun.shadow.camera.top = 50;
  sun.shadow.camera.bottom = -50;
  sun.shadow.camera.near = 0.5;
  sun.shadow.camera.far = 140;
  sun.shadow.bias = -0.0003;

  // Neon green fill — signature game accent
  const accent = new PointLight(0x53ffb5, 8.0, 45, 1.8);
  accent.position.set(-12, 6, -10);

  // Rim light — cool blue-purple from opposite side for depth
  const rim = new PointLight(0x6a5aff, 5.0, 40, 2.0);
  rim.position.set(18, 8, 12);

  scene.add(ambient, hemi, sun, accent, rim);
  lights = { ambient, hemi, sun, accent, rim };
  return lights;
}

export function getLighting() {
  if (!lights) {
    throw new Error('[lighting] initLighting() must run before getLighting().');
  }
  return lights;
}

export function updateAccentLight(position, offset = { x: -8, y: 6, z: -5 }) {
  if (!lights?.accent || !position) return;

  lights.accent.position.set(
    position.x + offset.x,
    offset.y,
    position.z + offset.z,
  );
}

export function setSunShadows(enabled, mapSize = 2048) {
  if (!lights?.sun) return;

  lights.sun.castShadow = enabled;
  lights.sun.shadow.mapSize.set(mapSize, mapSize);
  lights.sun.shadow.needsUpdate = true;
}

export function updateDayNightCycle(scene, elapsed = 0) {
  if (!lights) return;
  const cycle = (Math.sin(elapsed / 38) + 1) / 2;
  const day = cycle ** 1.4;
  lights.ambient.intensity = 0.28 + day * 0.82;
  lights.hemi.intensity = 0.62 + day * 1.25;
  lights.sun.intensity = 0.35 + day * 1.95;
  lights.sun.position.set(18 + day * 14, 14 + day * 34, -22 + day * 35);
  lights.accent.intensity = 9.5 - day * 3.2;
  lights.rim.intensity = 6.0 - day * 2.2;
  if (scene?.fog) {
    scene.fog.color.lerpColors(new Color(0x02040a), new Color(0x0a1221), day);
    scene.fog.density = 0.027 - day * 0.01;
  }
}
